import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2, FiSave, FiX, FiDownload, FiAlertTriangle } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import examService from "../../services/examService";
import subjectService from "../../services/subjectService";
import courseService from "../../services/courseService";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";

const TYPE_OPTIONS = ["Internal", "External", "Practical", "Sessional", "University", "Other"];
const STATUS_OPTIONS = ["Draft", "Active", "Results Published"];
const EMPTY_SUBJECT = { name: "", maxMarks: "", passingMarks: "" };

const EMPTY_FORM = {
    title: "",
    examType: "Internal",
    course: "",
    yearNumber: "",
    semesterNumber: "",
    courseAdmitted: "",
    courseYear: "",
    examDate: "",
    status: "Draft",
    notes: "",
};

function Field({ label, required, children, error }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}{required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

const normalizeExam = (payload) => {
    const exam = payload?.data ?? payload;
    if (!exam || Array.isArray(exam)) return null;
    return { ...exam, subjects: Array.isArray(exam.subjects) ? exam.subjects : [] };
};

export default function ExamForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { academicYear } = useApp();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [courses, setCourses] = useState([]);
    const [subjects, setSubjects] = useState([{ ...EMPTY_SUBJECT }]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    // Subject master for the selected course+year+semester
    const [masterSubjects, setMasterSubjects] = useState([]);  // [{ name, code, type }]
    const [masterLoading, setMasterLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await courseService.getAll({ isActive: true });
                setCourses(res.data || []);
            } catch (err) {
                toast.error("Couldn't load courses: " + err.message);
            }
        })();
    }, []);

    const selectedCourse = courses.find((c) => c._id === form.course);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];
    const semesterOptions = selectedCourse
        ? getSemesterOptions(selectedCourse, form.yearNumber)
        : [];
    const selectedYearOption = yearOptions.find((y) => y.yearNumber === Number(form.yearNumber));
    // Falls back to the legacy strings for exams predating the Course model —
    // this is what "Load All Subjects" actually queries against, so it must
    // resolve to something even before the form is saved.
    const courseLabel = selectedCourse?.shortName || form.courseAdmitted || "";
    const yearLabel = selectedYearOption?.label || form.courseYear || "";

    // ── Fetch subject master whenever course, year, or semester changes ──────
    const fetchMaster = useCallback(async (course, year, semesterNumber) => {
        if (!course || !year) { setMasterSubjects([]); return; }
        setMasterLoading(true);
        try {
            const params = { courseAdmitted: course, courseYear: year, isActive: true };
            if (semesterNumber) {
                params.semesterNumber = Number(semesterNumber);
            }
            const res = await subjectService.getAll(params);
            const list = res?.data || res || [];
            setMasterSubjects(Array.isArray(list) ? list : []);
        } catch {
            setMasterSubjects([]);
        } finally {
            setMasterLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMaster(courseLabel, yearLabel, form.semesterNumber);
    }, [courseLabel, yearLabel, form.semesterNumber, fetchMaster]);

    // ── Load existing exam in edit mode ──────────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        examService.getById(id)
            .then((res) => {
                const exam = normalizeExam(res);
                if (!exam) throw new Error("Exam details could not be loaded.");
                const { title, examType, course, yearNumber, semesterNumber, courseAdmitted, courseYear, examDate, status, notes, subjects: subs } = exam;
                setForm({
                    title, examType,
                    course: course || "",
                    yearNumber: yearNumber != null ? String(yearNumber) : "",
                    semesterNumber: semesterNumber != null ? String(semesterNumber) : "",
                    courseAdmitted, courseYear,
                    examDate: examDate || "", status, notes: notes || "",
                });
                setSubjects(subs.length > 0
                    ? subs.map((s) => ({ name: s.name, maxMarks: s.maxMarks, passingMarks: s.passingMarks }))
                    : [{ ...EMPTY_SUBJECT }]);
            })
            .catch((err) => { toast.error(err.message); navigate("/exams"); })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    // ── Form field change ─────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "course" ? { yearNumber: "", semesterNumber: "" } : {}),
            ...(name === "yearNumber" ? { semesterNumber: "" } : {}),
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
        // Reset subjects when course, year, or semester changes
        if (name === "course" || name === "yearNumber" || name === "semesterNumber") {
            setSubjects([{ ...EMPTY_SUBJECT }]);
        }
    };

    // ── Subject row helpers ───────────────────────────────────────────────────
    const updateSubject = (idx, field, value) =>
        setSubjects((prev) => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));

    const addSubject = () => setSubjects((prev) => [...prev, { ...EMPTY_SUBJECT }]);

    const removeSubject = (idx) => {
        if (subjects.length === 1) return toast.error("At least one subject is required.");
        setSubjects((prev) => prev.filter((_, i) => i !== idx));
    };

    // ── Load ALL subjects from master for this course+year ───────────────────
    const loadAllFromMaster = () => {
        if (!masterSubjects.length) return toast.error("No subjects found in master for this course and year.");
        // Keep only master subjects not already in the list
        const existing = new Set(subjects.map((s) => s.name).filter(Boolean));
        const toAdd = masterSubjects.filter((s) => !existing.has(s.name));

        if (!toAdd.length) return toast("All master subjects are already added.", { icon: "ℹ️" });

        const newRows = toAdd.map((s) => ({ name: s.name, maxMarks: "", passingMarks: "" }));
        // Replace empty first row if it's blank
        if (subjects.length === 1 && !subjects[0].name) {
            setSubjects(newRows);
        } else {
            setSubjects((prev) => [...prev, ...newRows]);
        }
        toast.success(`${toAdd.length} subject${toAdd.length !== 1 ? "s" : ""} loaded from master.`);
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.title.trim()) errs.title = "Title is required.";
        if (!form.examType) errs.examType = "Type is required.";
        if (!form.course) errs.course = "Course is required.";
        if (!form.yearNumber) errs.yearNumber = "Course year is required.";
        if (!academicYear?._id) errs._global = "Select an academic year from the Dashboard first.";

        subjects.forEach((s, i) => {
            if (!s.name.trim()) errs[`sub_name_${i}`] = "Select a subject.";
            if (s.maxMarks === "" || Number(s.maxMarks) < 1) errs[`sub_max_${i}`] = "Max marks required (min 1).";
            if (s.passingMarks === "" || Number(s.passingMarks) < 0) errs[`sub_pass_${i}`] = "Passing marks required.";
            if (Number(s.passingMarks) > Number(s.maxMarks)) errs[`sub_pass_${i}`] = "Passing marks cannot exceed max marks.";
        });

        // Duplicate subject check
        const names = subjects.map((s) => s.name.trim().toLowerCase()).filter(Boolean);
        const dupes = names.filter((n, i) => names.indexOf(n) !== i);
        if (dupes.length) errs._dupes = `Duplicate subject: "${dupes[0]}". Each subject can appear only once.`;

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const payload = {
                ...form,
                course: form.course || undefined,
                yearNumber: form.yearNumber !== "" ? Number(form.yearNumber) : undefined,
                semesterNumber: form.semesterNumber !== "" ? Number(form.semesterNumber) : undefined,
                academicYear: academicYear._id,
                subjects: subjects.map((s) => ({
                    name: s.name.trim(),
                    maxMarks: Number(s.maxMarks),
                    passingMarks: Number(s.passingMarks),
                })),
            };
            if (isEdit) {
                const res = await examService.update(id, payload);
                toast.success(res.message);
            } else {
                const res = await examService.create(payload);
                toast.success(res.message);
            }
            navigate("/exams");
        } catch (err) {
            toast.error(err.message || "Failed to save exam.");
        } finally {
            setLoading(false);
        }
    };

    const totalMax = subjects.reduce((s, sub) => s + (Number(sub.maxMarks) || 0), 0);
    const totalPass = subjects.reduce((s, sub) => s + (Number(sub.passingMarks) || 0), 0);

    // Names already picked (to disable in other rows)
    const pickedNames = new Set(subjects.map((s) => s.name).filter(Boolean));

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading exam…</div>;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Exam" : "Create Exam"}</h1>
                    <p className="page-subtitle">{academicYear?.label || "No academic year selected"}</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/exams")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Exam" : "Create Exam"}
                    </button>
                </div>
            </div>

            {errors._global && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} /> {errors._global}
                </div>
            )}

            <form onSubmit={handleSubmit}>

                {/* ── Exam Details ── */}
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Exam Details</div>
                    <div className="card-body">
                        <div className="form-grid">

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Exam Title <span style={{ color: "var(--danger)" }}>*</span></label>
                                <input name="title" value={form.title} onChange={handleChange}
                                    placeholder="e.g. Internal Test 1 — Theory"
                                    className={`form-control ${errors.title ? "error" : ""}`} autoFocus />
                                {errors.title && <span className="form-error">{errors.title}</span>}
                            </div>

                            <Field label="Exam Type" required error={errors.examType}>
                                <select name="examType" value={form.examType} onChange={handleChange}
                                    className={`form-control ${errors.examType ? "error" : ""}`}>
                                    {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </Field>

                            <Field label="Course" required error={errors.course}>
                                <select name="course" value={form.course} onChange={handleChange}
                                    className={`form-control ${errors.course ? "error" : ""}`}>
                                    <option value="">Select course…</option>
                                    {courses.map((c) => <option key={c._id} value={c._id}>{c.shortName}</option>)}
                                </select>
                            </Field>

                            <Field label="Course Year" required error={errors.yearNumber}>
                                <select name="yearNumber" value={form.yearNumber} onChange={handleChange}
                                    className={`form-control ${errors.yearNumber ? "error" : ""}`}
                                    disabled={!form.course}>
                                    <option value="">{form.course ? "Select year…" : "Select a course first"}</option>
                                    {yearOptions.map((y) => <option key={y.yearNumber} value={y.yearNumber}>{y.label}</option>)}
                                </select>
                            </Field>

                            <Field label="Semester" error={errors.semesterNumber}>
                                <select
                                    name="semesterNumber"
                                    value={form.semesterNumber}
                                    onChange={handleChange}
                                    className="form-control"
                                    disabled={!form.course || semesterOptions.length === 0}
                                >
                                    <option value="">{semesterOptions.length === 0 ? "No semesters" : "Select semester (optional)…"}</option>
                                    {semesterOptions.map((s) => (
                                        <option key={s.semesterNumber} value={s.semesterNumber}>
                                            {s.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Exam Date" error={errors.examDate}>
                                <input name="examDate" value={form.examDate} onChange={handleChange}
                                    placeholder="DD/MM/YYYY (optional)" className="form-control" />
                            </Field>

                            <Field label="Status">
                                <select name="status" value={form.status} onChange={handleChange} className="form-control">
                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Field>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Notes (optional)</label>
                                <textarea name="notes" value={form.notes} onChange={handleChange}
                                    rows={2} placeholder="Any additional notes…"
                                    className="form-control" style={{ resize: "vertical" }} />
                            </div>

                        </div>
                    </div>
                </div>

                {/* ── Subjects & Marks ── */}
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                        <span>Subjects & Marks</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
                            {totalMax > 0 && (
                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    Total: <strong style={{ color: "var(--text)" }}>{totalMax}</strong> max ·{" "}
                                    <strong style={{ color: "#16a34a" }}>{totalPass}</strong> passing
                                </span>
                            )}
                            {/* Load all from master */}
                            {courseLabel && yearLabel && (
                                <button type="button" className="btn btn-outline btn-sm"
                                    onClick={loadAllFromMaster}
                                    disabled={masterLoading}
                                    title="Auto-fill all subjects from the Subject master for this course and year"
                                    style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                    <FiDownload size={12} />
                                    {masterLoading ? "Loading…" : "Load All Subjects"}
                                </button>
                            )}
                            <button type="button" className="btn btn-outline btn-sm" onClick={addSubject}
                                style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                <FiPlus size={12} /> Add Row
                            </button>
                        </div>
                    </div>

                    <div className="card-body">

                        {/* Info / warnings */}
                        {!courseLabel || !yearLabel ? (
                            <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#002747" }}>
                                Select a Course and Course Year above to load subjects from the Subject master.
                            </div>
                        ) : masterLoading ? (
                            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem" }}>Loading subjects for {courseLabel} {yearLabel}…</div>
                        ) : masterSubjects.length === 0 ? (
                            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiAlertTriangle size={14} />
                                No subjects found in master for {courseLabel} — {yearLabel}.
                                <a href="/subjects/new" style={{ color: "#92400e", fontWeight: 700, marginLeft: 4 }} target="_blank" rel="noreferrer">Add subjects →</a>
                            </div>
                        ) : (
                            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
                                {masterSubjects.length} subject{masterSubjects.length !== 1 ? "s" : ""} available in master for {courseLabel} — {yearLabel}.
                                Use <strong>Load All Subjects</strong> to auto-fill, or add rows individually.
                            </div>
                        )}

                        {errors._dupes && (
                            <div style={{ color: "#dc2626", fontSize: 12.5, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: 6 }}>
                                <FiAlertTriangle size={13} /> {errors._dupes}
                            </div>
                        )}

                        {/* Column headers */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 130px 40px", gap: "0.5rem", marginBottom: "0.4rem" }}>
                            <span className="form-label">Subject Name *</span>
                            <span className="form-label">Max Marks *</span>
                            <span className="form-label">Average Marks *</span>
                            <span />
                        </div>

                        {/* Subject rows */}
                        {subjects.map((sub, idx) => (
                            <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 130px 130px 40px", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "start" }}>

                                {/* Subject name — dropdown if master has subjects, text input as fallback */}
                                <div>
                                    {masterSubjects.length > 0 ? (
                                        <select
                                            value={sub.name}
                                            onChange={(e) => updateSubject(idx, "name", e.target.value)}
                                            className={`form-control ${errors[`sub_name_${idx}`] ? "error" : ""}`}
                                        >
                                            <option value="">— Select subject —</option>
                                            {masterSubjects.map((s) => (
                                                <option
                                                    key={s._id}
                                                    value={s.name}
                                                    // Disable options already selected in other rows
                                                    disabled={pickedNames.has(s.name) && sub.name !== s.name}
                                                >
                                                    {s.name}{s.code ? ` (${s.code})` : ""}{s.type !== "Theory" ? ` — ${s.type}` : ""}
                                                    {pickedNames.has(s.name) && sub.name !== s.name ? " ✓" : ""}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            value={sub.name}
                                            onChange={(e) => updateSubject(idx, "name", e.target.value)}
                                            placeholder={`Subject name ${idx + 1}`}
                                            className={`form-control ${errors[`sub_name_${idx}`] ? "error" : ""}`}
                                        />
                                    )}
                                    {errors[`sub_name_${idx}`] && <span className="form-error">{errors[`sub_name_${idx}`]}</span>}
                                </div>

                                {/* Max marks */}
                                <div>
                                    <input type="number" min="1"
                                        value={sub.maxMarks}
                                        onChange={(e) => updateSubject(idx, "maxMarks", e.target.value)}
                                        placeholder="e.g. 80"
                                        className={`form-control ${errors[`sub_max_${idx}`] ? "error" : ""}`}
                                    />
                                    {errors[`sub_max_${idx}`] && <span className="form-error">{errors[`sub_max_${idx}`]}</span>}
                                </div>

                                {/* Passing marks */}
                                <div>
                                    <input type="number" min="0"
                                        value={sub.passingMarks}
                                        onChange={(e) => updateSubject(idx, "passingMarks", e.target.value)}
                                        placeholder="e.g. 36"
                                        className={`form-control ${errors[`sub_pass_${idx}`] ? "error" : ""}`}
                                    />
                                    {errors[`sub_pass_${idx}`] && <span className="form-error">{errors[`sub_pass_${idx}`]}</span>}
                                </div>

                                {/* Remove */}
                                <button type="button" onClick={() => removeSubject(idx)}
                                    disabled={subjects.length === 1}
                                    title="Remove subject"
                                    style={{ height: 38, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "#dc2626", cursor: subjects.length === 1 ? "not-allowed" : "pointer", opacity: subjects.length === 1 ? 0.4 : 1 }}>
                                    <FiTrash2 size={13} />
                                </button>
                            </div>
                        ))}

                        <button type="button" className="btn btn-outline btn-sm" onClick={addSubject}
                            style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                            <FiPlus size={12} /> Add Another Subject
                        </button>
                    </div>
                </div>

                {/* ── Actions ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/exams")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        <FiSave size={14} /> {loading ? "Saving…" : isEdit ? "Update Exam" : "Create Exam"}
                    </button>
                </div>
            </form>
        </div>
    );
}